import axios from 'axios';

// ============================================================
// K-1 Verification Token Storage Utilities
// ============================================================

/**
 * Check if localStorage is available (returns false in private browsing mode or when blocked)
 * @returns {boolean}
 */
const isLocalStorageAvailable = () => {
  try {
    const testKey = '__localStorage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Simple hash function for email addresses (for storage key obfuscation)
 * @param {string} str - String to hash
 * @returns {string} Base64 hash
 */
const simpleHash = str => {
  // Simple hash using btoa for basic PII obfuscation in storage keys
  // Not cryptographically secure, but prevents casual PII exposure in devtools
  try {
    return btoa(str)
      .replace(/[+/=]/g, '')
      .substring(0, 16);
  } catch (e) {
    // Fallback if btoa fails
    return str
      .split('')
      .reduce((hash, char) => {
        return ((hash << 5) - hash + char.charCodeAt(0)) | 0;
      }, 0)
      .toString(36);
  }
};

/**
 * Generate storage key for K-1 verification token
 * @param {number} taxReturnId - The tax return ID
 * @param {string} partnerEmail - The partner's email address
 * @returns {string} Storage key (with hashed email for PII protection)
 */
const getK1VerificationStorageKey = (taxReturnId, partnerEmail) => {
  // Hash email to prevent PII exposure in localStorage keys
  const emailHash = simpleHash(partnerEmail);
  return `k1_verification_${taxReturnId}_${emailHash}`;
};

/**
 * Store K-1 verification token in localStorage
 * @param {number} taxReturnId - The tax return ID
 * @param {string} partnerEmail - The partner's email address
 * @param {string} token - The verification token
 * @param {number} expiresAt - Unix timestamp when token expires
 */
const storeK1VerificationToken = (
  taxReturnId,
  partnerEmail,
  token,
  expiresAt,
) => {
  if (!taxReturnId || !partnerEmail || !token || !expiresAt) {
    console.error('Invalid parameters for K-1 verification token storage');
    return;
  }
  if (!isLocalStorageAvailable()) {
    console.warn(
      'localStorage is not available (possibly private browsing mode)',
    );
    return;
  }
  const storageKey = getK1VerificationStorageKey(taxReturnId, partnerEmail);
  try {
    localStorage.setItem(
      storageKey,
      JSON.stringify({
        token,
        expiresAt,
      }),
    );
  } catch (error) {
    console.error('Failed to store K-1 verification token:', error.message);
  }
};

/**
 * Get K-1 verification token from localStorage
 * @param {number} taxReturnId - The tax return ID
 * @param {string} partnerEmail - The partner's email address
 * @returns {{ token: string, expiresAt: number } | null} Token data or null if not found/expired
 */
const getK1VerificationToken = (taxReturnId, partnerEmail) => {
  if (!isLocalStorageAvailable()) {
    return null;
  }
  const storageKey = getK1VerificationStorageKey(taxReturnId, partnerEmail);

  try {
    const dataString = localStorage.getItem(storageKey);
    if (!dataString) {
      return null;
    }

    const data = JSON.parse(dataString);
    const now = Math.floor(Date.now() / 1000);

    // Check if token has expired
    if (now >= data.expiresAt) {
      // Token expired - remove it
      localStorage.removeItem(storageKey);
      return null;
    }

    return data;
  } catch (error) {
    // Invalid JSON or localStorage error - try to clean up
    console.warn(
      'Invalid K-1 verification token data in localStorage:',
      error.message,
    );
    try {
      localStorage.removeItem(storageKey);
    } catch (e) {
      // Ignore cleanup errors
    }
    return null;
  }
};

/**
 * Remove K-1 verification token from localStorage
 * @param {number} taxReturnId - The tax return ID
 * @param {string} partnerEmail - The partner's email address
 */
const removeK1VerificationToken = (taxReturnId, partnerEmail) => {
  if (!isLocalStorageAvailable()) {
    return;
  }
  const storageKey = getK1VerificationStorageKey(taxReturnId, partnerEmail);
  try {
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.warn('Failed to remove K-1 verification token:', error.message);
  }
};

/**
 * Handle verification token errors from protected endpoints
 * Removes invalid tokens from localStorage and re-throws the error
 * @param {Error} error - The axios error object
 * @param {number} taxReturnId - The tax return ID
 * @param {string} partnerEmail - The partner's email address
 * @throws {Error} Re-throws the original error after cleanup
 */
const handleVerificationTokenError = (error, taxReturnId, partnerEmail) => {
  // Check if this is a verification_required error (invalid/expired token)
  if (
    error.response &&
    error.response.status === 403 &&
    error.response.data &&
    error.response.data.error === 'verification_required'
  ) {
    // Remove invalid token from localStorage
    if (taxReturnId && partnerEmail) {
      removeK1VerificationToken(taxReturnId, partnerEmail);
    }
  }
  throw error;
};

// ============================================================
// Tax Return API Methods
// ============================================================

const getTaxReturn = ({ id, companyId }) => {
  return axios.get(`/tax_returns/${id}`, {
    params: {
      company_id: companyId,
    },
  });
};

const getTaxReturns = ({ companyId, year }) => {
  return axios.get('/tax_returns', {
    params: {
      company_id: companyId,
      year,
    },
  });
};

const getActivityLogs = ({ id, companyId }) => {
  return axios.get(`/tax_returns/${id}/get_activity_logs`, {
    params: {
      company_id: companyId,
    },
  });
};

const saveSignature = ({
  id,
  companyId,
  formIndex,
  signatureIndex,
  signatureType,
  signatureData,
  signatureRawData,
  signatureDate,
  sessionId,
  userId,
  title,
  name,
  email,
  signatureLeft,
  signatureBottom,
  signatureWidth,
  signatureHeight,
}) => {
  return axios.post(`/tax_returns/${id}/save_signature`, {
    company_id: companyId,
    form_index: formIndex,
    signature_index: signatureIndex,
    signature: {
      signatureType: signatureType,
      signatureData: signatureData,
      signatureRawData: signatureRawData,
      signatureDate: signatureDate,
      signatureLeft: signatureLeft,
      signatureBottom: signatureBottom,
      signatureWidth: signatureWidth,
      signatureHeight: signatureHeight,
      userId,
      title,
      name,
      email,
    },
    session_id: sessionId,
  });
};

const createCheckoutSession = ({ id, companyId }) => {
  return axios.post(`/tax_returns/${id}/create_checkout_session`, {
    company_id: companyId,
  });
};

const verifyCheckoutSession = ({ id, companyId }) => {
  return axios.post(`/tax_returns/${id}/verify_checkout_session`, {
    company_id: companyId,
  });
};

const sendToDocuments = ({
  id,
  companyId,
  subject,
  message,
  documentName,
  path,
  saveSubjectForFutureUse,
  saveMessageForFutureUse,
}) => {
  return axios.post(`/tax_returns/${id}/send_to_documents`, {
    company_id: companyId,
    subject,
    message,
    document_name: documentName,
    path: path,
  });
};

const downloadTaxReturn = ({ id, companyId, includeOtherPages = false }) => {
  return axios.post(`/tax_returns/${id}/download_tax_return`, {
    company_id: companyId,
    include_other_pages: includeOtherPages,
  });
};

const getOriginalImages = ({ id, companyId }) => {
  return axios.get(`/tax_returns/${id}/get_original_images`, {
    params: {
      company_id: companyId,
    },
  });
};

const updateTaxReturn = ({ id, companyId, ...data }) => {
  return axios.patch(`/tax_returns/${id}`, {
    company_id: companyId,
    ...data,
  });
};

const getEstimatedOcrTime = ({ id, companyId }) => {
  return axios.get(`/tax_returns/${id}/get_estimated_ocr_time`, {
    params: {
      company_id: companyId,
    },
  });
};

const getUploadLinkForPaymentVoucher = ({ companyId }) => {
  return axios.get(`/tax_returns/get_upload_link_for_payment_voucher`, {
    params: {
      company_id: companyId,
    },
  });
};

const processPaymentVoucher = ({
  fileId,
  companyId,
  taxReturnId,
  documentName,
}) => {
  return axios.post(`/tax_returns/${taxReturnId}/process_payment_voucher`, {
    file_id: fileId,
    company_id: companyId,
    document_name: documentName,
  });
};

const getUploadLinkForPaperFiledReturn = ({ companyId }) => {
  return axios.get(`/tax_returns/get_upload_link_for_paper_filed_return`, {
    params: {
      company_id: companyId,
    },
  });
};

const getDownloadLinkForPaperFiledReturn = ({ companyId, documentPath }) => {
  return axios.get(`/tax_returns/get_download_link_for_paper_filed_return`, {
    params: {
      company_id: companyId,
      document_path: documentPath,
    },
  });
};

const processPaperFiledReturn = ({
  fileId,
  companyId,
  taxReturnId,
  documentName,
}) => {
  return axios.post(`/tax_returns/${taxReturnId}/process_paper_filed_return`, {
    file_id: fileId,
    company_id: companyId,
    document_name: documentName,
  });
};

const getUploadLinkForK1Package = ({ companyId }) => {
  return axios.get(`/tax_returns/get_upload_link_for_k1_package`, {
    params: {
      company_id: companyId,
    },
  });
};

const processK1Package = ({ fileId, companyId, taxReturnId, documentName }) => {
  return axios.post(`/tax_returns/${taxReturnId}/process_k1_package`, {
    file_id: fileId,
    company_id: companyId,
    document_name: documentName,
  });
};

const getUploadLinkForK1Spreadsheet = ({ companyId }) => {
  return axios.get(`/tax_returns/get_upload_link_for_k1_spreadsheet`, {
    params: {
      company_id: companyId,
    },
  });
};

const processK1Spreadsheet = ({
  fileId,
  companyId,
  taxReturnId,
  documentName,
  partnerNames,
}) => {
  return axios.post(`/tax_returns/${taxReturnId}/process_k1_spreadsheet`, {
    file_id: fileId,
    company_id: companyId,
    document_name: documentName,
    partner_names: partnerNames,
  });
};

const getK1PackageDownloadLink = ({ token, taxReturnId, partnerEmail }) => {
  const headers = {};

  // Include verification token if available in localStorage
  if (taxReturnId && partnerEmail) {
    const verificationData = getK1VerificationToken(taxReturnId, partnerEmail);
    if (verificationData && verificationData.token) {
      headers['X-Verification-Token'] = verificationData.token;
    }
  }

  return axios
    .get(`/tax_returns/get_k1_package_download_link`, {
      params: {
        token: token,
      },
      headers,
    })
    .catch(error =>
      handleVerificationTokenError(error, taxReturnId, partnerEmail),
    );
};

const resendK1PackageEmail = ({ token }) => {
  return axios.get(`/tax_returns/resend_k1_package_email`, {
    params: {
      token: token,
    },
  });
};

const updateK1ElectronicConsent = ({
  token,
  consented,
  taxReturnId,
  partnerEmail,
}) => {
  const headers = {};

  // Include verification token if available in localStorage
  if (taxReturnId && partnerEmail) {
    const verificationData = getK1VerificationToken(taxReturnId, partnerEmail);
    if (verificationData && verificationData.token) {
      headers['X-Verification-Token'] = verificationData.token;
    }
  }

  return axios
    .get(`/tax_returns/update_k1_electronic_consent`, {
      params: {
        token: token,
        consented: consented,
      },
      headers,
    })
    .catch(error => {
      handleVerificationTokenError(error, taxReturnId, partnerEmail);
    });
};

const sendK1PackageToPartner = ({
  company_id,
  tax_return_id,
  document_path,
  partner_email,
  partner_name,
}) => {
  return axios.post(
    `/tax_returns/${tax_return_id}/send_k1_package_to_partner`,
    {
      company_id,
      document_path,
      partner_email,
      partner_name,
    },
  );
};

/**
 * Check K-1 verification status for a tax return
 * @param {Object} params
 * @param {number} params.taxReturnId - The tax return ID
 * @param {string} params.token - JWT token for authentication
 * @param {string} params.partnerEmail - The partner's email address (for token checking)
 * @returns {Promise} Response with verification_enabled, verified, bypass_verification, partner_email
 */
const checkK1Verification = ({ taxReturnId, token, partnerEmail }) => {
  // Check localStorage first for valid verification token
  if (partnerEmail) {
    const verificationData = getK1VerificationToken(taxReturnId, partnerEmail);
    if (verificationData && verificationData.token) {
      // We have a valid token - include it in the request
      return axios.get(`/tax_returns/${taxReturnId}/check_k1_verification`, {
        params: {
          token: token,
        },
        headers: {
          'X-Verification-Token': verificationData.token,
        },
      });
    }
  }

  // No valid token in storage - proceed with regular check
  return axios.get(`/tax_returns/${taxReturnId}/check_k1_verification`, {
    params: {
      token: token,
    },
  });
};

/**
 * Request a verification code to be sent via email
 * @param {Object} params
 * @param {number} params.taxReturnId - The tax return ID
 * @param {string} params.token - JWT token for authentication
 * @returns {Promise} Response with expires_in_seconds
 */
const requestK1VerificationCode = ({ taxReturnId, token }) => {
  return axios.post(
    `/tax_returns/${taxReturnId}/request_k1_verification_code`,
    { token: token },
  );
};

/**
 * Verify the 6-character alphanumeric code sent via email
 * @param {Object} params
 * @param {number} params.taxReturnId - The tax return ID
 * @param {string} params.token - JWT token for authentication
 * @param {string} params.code - The 6-character alphanumeric verification code (A-Z, 0-9).
 *                                 Case-insensitive - backend accepts both uppercase and lowercase.
 * @param {string} params.partnerEmail - The partner's email address (for token storage)
 * @returns {Promise} Response with verification_token and expires_at
 */
const verifyK1Code = ({ taxReturnId, token, code, partnerEmail }) => {
  return axios
    .post(`/tax_returns/${taxReturnId}/verify_k1_code`, {
      token: token,
      code: code,
    })
    .then(response => {
      // Store verification token if received from backend
      if (
        response.data.verification_token &&
        response.data.expires_at &&
        partnerEmail
      ) {
        try {
          storeK1VerificationToken(
            taxReturnId,
            partnerEmail,
            response.data.verification_token,
            response.data.expires_at,
          );
        } catch (storageError) {
          // Non-fatal: token storage failed but verification succeeded
          // Backend will handle re-verification if needed on next request
          console.warn('Failed to store verification token:', storageError);
        }
      }
      return response;
    });
};

/**
 * Unlock a K-1 package that was locked due to too many failed verification attempts
 * @param {Object} params
 * @param {number} params.companyId - The company ID
 * @param {number} params.taxReturnId - The tax return ID
 * @param {string} params.documentPath - The document path of the K-1 package
 * @param {string} params.partnerEmail - The partner's email address
 * @returns {Promise} Response with unlocked status
 */
const unlockK1Verification = ({
  companyId,
  taxReturnId,
  documentPath,
  partnerEmail,
}) => {
  return axios.post(
    `/companies/${companyId}/tax_returns/${taxReturnId}/unlock_k1_verification`,
    {
      document_path: documentPath,
      partner_email: partnerEmail,
    },
  );
};

const getTaxReturnExtraction = ({ companyId, source }) => {
  return axios.get('/tax_return_extractions/get_extraction_by_source', {
    params: {
      source: source,
      company_id: companyId,
    },
  });
};

const getSignaturePlacement = ({ id, companyId, page, title }) => {
  return axios.get(`/tax_returns/${id}/get_signature_placement`, {
    params: {
      company_id: companyId,
      page: page,
      title: title,
    },
  });
};

const processDocuments = ({
  ids,
  documentName,
  path,
  companyId,
  taxYear,
  taxSummaryEnabled,
}) => {
  return axios.post('/tax_returns/process_documents', {
    file_ids: ids,
    document_name: documentName,
    path,
    company_id: companyId,
    tax_year: taxYear,
    tax_summary_enabled: taxSummaryEnabled,
  });
};

const collectSignatureDataForForm = ({ id, companyId, formIndex }) => {
  return axios.post(`/tax_returns/${id}/collect_signature_data_for_form`, {
    company_id: companyId,
    form_index: formIndex,
  });
};

const getDefaultSignatureValues = () => {
  return axios.get('/tax_returns/get_default_signature_values');
};

const analyzeFilingInstructions = ({ id, companyId, pages }) => {
  return axios.post(`/tax_returns/${id}/analyze_filing_instructions`, {
    company_id: companyId,
    pages: pages,
  });
};

export default {
  // Verification token storage utilities
  getK1VerificationStorageKey,
  storeK1VerificationToken,
  getK1VerificationToken,
  removeK1VerificationToken,
  // API methods
  getTaxReturn,
  getTaxReturns,
  getActivityLogs,
  saveSignature,
  createCheckoutSession,
  verifyCheckoutSession,
  sendToDocuments,
  downloadTaxReturn,
  getOriginalImages,
  updateTaxReturn,
  getEstimatedOcrTime,
  getUploadLinkForPaymentVoucher,
  processPaymentVoucher,
  getUploadLinkForPaperFiledReturn,
  getDownloadLinkForPaperFiledReturn,
  processPaperFiledReturn,
  getUploadLinkForK1Package,
  processK1Package,
  getUploadLinkForK1Spreadsheet,
  processK1Spreadsheet,
  getK1PackageDownloadLink,
  resendK1PackageEmail,
  updateK1ElectronicConsent,
  sendK1PackageToPartner,
  checkK1Verification,
  requestK1VerificationCode,
  verifyK1Code,
  unlockK1Verification,
  getTaxReturnExtraction,
  getSignaturePlacement,
  getDefaultSignatureValues,
  processDocuments,
  collectSignatureDataForForm,
  analyzeFilingInstructions,
};
